'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { ReactNode } from 'react';

type RealtyOfferValue =
  | string
  | number
  | null
  | undefined
  | {
      id?: string | number | null;
      code?: string | null;
      slug?: string | null;
      name?: string | null;
    };

type RealtyOfferTone = 'sale' | 'rent' | 'buy' | 'default';

export interface RealtyBadgeProps {
  offer_type?: RealtyOfferValue;
  label: string;
  icon?: ReactNode;
  badge_type: 'preview' | 'detail';
  className?: string;
}

const normalizeOfferValue = (value: string | number | null | undefined) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const resolveOfferTone = (offerType: RealtyOfferValue): RealtyOfferTone => {
  if (offerType && typeof offerType === 'object') {
    const idTone = resolveOfferTone(offerType.id);
    if (idTone !== 'default') return idTone;

    return resolveOfferTone(offerType.code ?? offerType.slug ?? offerType.name);
  }

  const semanticValue = normalizeOfferValue(offerType);

  if (['2', 'sale', 'sell', 'predaj'].includes(semanticValue)) return 'sale';
  if (['1', 'rent', 'rental', 'prenajom'].includes(semanticValue)) return 'rent';
  if (['buy', 'purchase', 'kupa'].includes(semanticValue)) return 'buy';

  return 'default';
};

export const RealtyBadge = ({
  offer_type,
  icon,
  label,
  badge_type,
  className,
}: RealtyBadgeProps) => {
  const offerTone = resolveOfferTone(offer_type);

  return (
    <div
      className={cn(
        'ea-realty-badge inline-flex w-fit max-w-full items-center justify-center gap-1 whitespace-nowrap rounded-full font-normal',
        badge_type === 'preview' &&
          'min-h-6 shrink-0 px-2.5 py-1 text-[10px] leading-4 xl:min-h-[23px] xl:py-0.5 xl:text-[12px] xl:leading-[1.6]',
        badge_type === 'detail' && 'px-4 py-1 text-sm leading-normal',
        offerTone === 'default' && 'bg-card text-primary ring-1 ring-inset ring-border',
        offerTone === 'sale' && 'bg-[#4E8AFF] text-white',
        offerTone === 'rent' && 'bg-[#FFB800] text-white',
        offerTone === 'buy' && 'bg-secondary text-secondary-foreground',
        className,
      )}
      data-eli-offer-tone={offerTone}
      data-eli-part="badge"
    >
      {icon}
      <span>{label}</span>
    </div>
  );
};
